import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  Input,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { getMarketingOverrideApi, saveMarketingOverrideApi } from 'api/marketingApi';
import Card from 'components/card/Card';
import GroupedAmountInput from 'components/GroupedAmountInput';
import { parseGroupedNumber } from 'components/groupedNumber';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';

const COLUMN_STYLES = {
  month: { width: '160px', minWidth: '160px' },
  source: { flex: 1, minWidth: '140px' },
  spend: { width: '200px', minWidth: '180px' },
  predicted: { width: '160px', minWidth: '140px' },
  actions: { width: '40px' },
};

function lastDayOfMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function protoDateFromMoment(m) {
  return { year: m.year(), month: m.month() + 1, day: 1 };
}

function monthLabel(protoDate) {
  return moment({ year: protoDate.year, month: protoDate.month - 1, day: 1 }).format('MMM YYYY');
}

function SpendAmountInput({ value, onCommit, ariaLabel }) {
  const [draft, setDraft] = useState(value ?? '');
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(value ?? '');
  }, [value, focused]);
  return (
    <Box position="relative">
      <GroupedAmountInput
        width="full"
        pl={7}
        aria-label={ariaLabel}
        value={draft}
        onFocus={() => setFocused(true)}
        onChange={(next) => {
          setDraft(next);
          if (next === '' || next === '.' || next.endsWith('.')) return;
          const amount = Number(next);
          if (Number.isFinite(amount)) onCommit(amount);
        }}
        onBlur={() => {
          setFocused(false);
          const raw = parseGroupedNumber(draft);
          onCommit(raw === '' || raw === '.' ? 0 : Number(raw));
        }}
      />
      <Box position="absolute" left={2} top="50%" transform="translateY(-50%)" pointerEvents="none" fontSize="sm" color="gray.500">$</Box>
    </Box>
  );
}

// embedded: always open and without the outer margin, for use inside a popup.
// onSaved: called after the rows are saved.
export default function MarketingSpendSection({ customerId, embedded = false, onSaved }) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const [storedExpanded, setExpanded] = useState(
    () => localStorage.getItem('marketingSpendSectionExpanded') === 'true'
  );
  const expanded = embedded || storedExpanded;
  const [rows, setRows] = useState([]);
  const [newRow, setNewRow] = useState({ monthInput: '', source: '', uaCost: 0, isPrediction: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    getMarketingOverrideApi({ customerId })
      .then((data) => {
        setRows((data || []).map((r) => ({ ...r, _dirty: false })));
      })
      .catch(() => {
        toast({ title: 'Failed to load marketing spend data', status: 'error', duration: 4000 });
      })
      .finally(() => setLoading(false));
  }, [customerId]);

  function handleRowChange(index, field, value) {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value, _dirty: true };
      return updated;
    });
    setSaved(false);
  }

  function handleDeleteRow(index) {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setSaved(false);
  }

  function handleAddRow() {
    if (!newRow.monthInput) return;
    const m = moment(newRow.monthInput, 'YYYY-MM-DD');
    if (!m.isValid()) return;
    const protoMonth = protoDateFromMoment(m);
    const exists = rows.some(
      (r) => r.month.year === protoMonth.year && r.month.month === protoMonth.month && r.source === newRow.source,
    );
    if (exists) {
      toast({ title: 'A row for that month and source already exists', status: 'warning', duration: 3000 });
      return;
    }
    setRows((prev) => [...prev, { month: protoMonth, source: newRow.source, ua_cost: newRow.uaCost, is_prediction: newRow.isPrediction, _dirty: true }]);
    setNewRow({ monthInput: '', source: '', uaCost: 0, isPrediction: false });
    setSaved(false);
  }

  async function doSave() {
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        month: r.month,
        source: r.source,
        ua_cost: parseFloat(r.ua_cost) || 0,
        is_prediction: r.is_prediction,
      }));
      await saveMarketingOverrideApi({ customerId, rows: payload });
      setSaved(true);
      toast({ title: 'Marketing spend saved', status: 'success', duration: 3000 });
      onSaved?.();
    } catch {
      toast({ title: 'Failed to save marketing spend', status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
      onClose();
    }
  }

  const totalSpend = rows.reduce((sum, r) => sum + (parseFloat(r.ua_cost) || 0), 0);
  const predictedCount = rows.filter((r) => r.is_prediction).length;

  return (
    <Box mt={embedded ? 0 : 8}>
      <Card p={embedded ? 0 : 6}>
        <Flex
          justify="space-between"
          align="center"
          onClick={embedded ? undefined : () => setExpanded((v) => { const next = !v; localStorage.setItem('marketingSpendSectionExpanded', String(next)); return next; })}
          cursor={embedded ? undefined : 'pointer'}
          mb={expanded ? 4 : 0}
        >
          <Box>
            <Text fontWeight="bold" fontSize="lg">Manual Marketing Spend By Source</Text>
          </Box>
          <Flex align="center" gap={3}>
            {expanded && (
              <Button
                colorScheme="purple"
                size="sm"
                isLoading={saving}
                isDisabled={loading}
                onClick={(e) => { e.stopPropagation(); onOpen(); }}
              >
                {saved ? 'Saved' : 'Save'}
              </Button>
            )}
            {!embedded && <Text fontSize="sm" color="gray.500">{expanded ? '▲' : '▼'}</Text>}
          </Flex>
        </Flex>

        <Collapse in={expanded} animateOpacity>

        {/* Header */}
        <Flex gap={4} px={2} pb={2} borderBottom="1px solid" borderColor="gray.100">
          <Box style={COLUMN_STYLES.month}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase">Month</Text>
          </Box>
          <Box style={COLUMN_STYLES.source}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase">Source</Text>
          </Box>
          <Box style={COLUMN_STYLES.spend}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase">Spend (USD)</Text>
          </Box>
          <Box style={COLUMN_STYLES.predicted}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase">Predicted</Text>
          </Box>
          <Box style={COLUMN_STYLES.actions} />
        </Flex>

        {/* Existing rows */}
        {loading ? (
          <Text px={2} py={4} color="gray.400" fontSize="sm">Loading...</Text>
        ) : rows.length === 0 && (
          <Text px={2} py={4} color="gray.400" fontSize="sm">No entries yet. Add a month below.</Text>
        )}
        {rows.map((row, index) => (
          <Flex key={index} gap={4} px={2} py={2} align="center" borderBottom="1px solid" borderColor="gray.50" _hover={{ bg: 'gray.50' }}>
            <Box style={COLUMN_STYLES.month}>
              <Text fontWeight="bold" fontSize="sm">{monthLabel(row.month)}</Text>
            </Box>
            <Box style={COLUMN_STYLES.source}>
              <FormControl>
                <Input
                  size="sm"
                  value={row.source}
                  onChange={(e) => handleRowChange(index, 'source', e.target.value)}
                  placeholder="e.g. Google"
                />
              </FormControl>
            </Box>
            <Box style={COLUMN_STYLES.spend}>
              <SpendAmountInput
                value={row.ua_cost}
                ariaLabel={`Spend for ${monthLabel(row.month)}`}
                onCommit={(amount) => handleRowChange(index, 'ua_cost', amount)}
              />
            </Box>
            <Box style={COLUMN_STYLES.predicted}>
              <Flex align="center" gap={2}>
                <Switch
                  colorScheme="purple"
                  isChecked={row.is_prediction}
                  onChange={(e) => handleRowChange(index, 'is_prediction', e.target.checked)}
                />
                <Text fontSize="sm" color={row.is_prediction ? 'purple.500' : 'gray.500'} fontWeight={row.is_prediction ? 'semibold' : 'normal'}>
                  {row.is_prediction ? 'Predicted' : 'Actual'}
                </Text>
              </Flex>
            </Box>
            <Box style={COLUMN_STYLES.actions}>
              <Button variant="ghost" size="xs" color="gray.400" _hover={{ color: 'red.400' }} onClick={() => handleDeleteRow(index)}>✕</Button>
            </Box>
          </Flex>
        ))}

        {/* New row form */}
        <Flex gap={4} px={2} py={3} mt={1} align="center" bg="gray.50" borderRadius="md">
          <Box style={COLUMN_STYLES.month}>
            <Input
              size="sm"
              type="date"
              value={newRow.monthInput}
              onChange={(e) => setNewRow((p) => ({ ...p, monthInput: e.target.value }))}
            />
          </Box>
          <Box style={COLUMN_STYLES.source}>
            <Input
              size="sm"
              placeholder="e.g. Facebook"
              value={newRow.source}
              onChange={(e) => setNewRow((p) => ({ ...p, source: e.target.value }))}
            />
          </Box>
          <Box style={COLUMN_STYLES.spend}>
            <SpendAmountInput
              value={newRow.uaCost}
              ariaLabel="New marketing spend"
              onCommit={(amount) => setNewRow((current) => ({ ...current, uaCost: amount }))}
            />
          </Box>
          <Box style={COLUMN_STYLES.predicted}>
            <Flex align="center" gap={2}>
              <Switch
                colorScheme="purple"
                isChecked={newRow.isPrediction}
                onChange={(e) => setNewRow((p) => ({ ...p, isPrediction: e.target.checked }))}
              />
              <Text fontSize="sm" color={newRow.isPrediction ? 'purple.500' : 'gray.500'}>
                {newRow.isPrediction ? 'Predicted' : 'Actual'}
              </Text>
            </Flex>
          </Box>
          <Box style={COLUMN_STYLES.actions}>
            <Button colorScheme="purple" size="sm" onClick={handleAddRow} isDisabled={!newRow.monthInput}>+</Button>
          </Box>
        </Flex>

        {/* Footer */}
        <Flex justify="space-between" align="center" mt={4} px={2}>
          <Text fontSize="sm" color="gray.500">
            {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
            {predictedCount > 0 && ` · ${predictedCount} predicted`}
          </Text>
          <Text fontSize="sm">
            Total spend:{' '}
            <Text as="span" color="blue.500" fontWeight="bold">
              ${totalSpend.toLocaleString()}
            </Text>
          </Text>
        </Flex>

        </Collapse>
      </Card>

      {/* Save confirmation dialog */}
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Save Marketing Spend</AlertDialogHeader>
            <AlertDialogBody>
              Save {rows.length} {rows.length === 1 ? 'entry' : 'entries'} to BigQuery? This will append all rows with the current timestamp. The most recent data per month and source will be used.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>Cancel</Button>
              <Button colorScheme="purple" onClick={doSave} isLoading={saving} ml={3}>Save</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
